import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Moon, Settings as SettingsIcon, Sun } from 'lucide-react';
import { Avatar } from './avatar';
import { useTheme } from '@/lib/theme';
import { useLogout } from '@/lib/auth';
import { cn } from '@/lib/cn';

/**
 * Account dropdown shared by the admin and portal topbars. Settings is admin-
 * only (no portal settings route yet), so it's opt-in via `showSettings`.
 */
export function AvatarMenu({
  name,
  avatarUrl,
  showSettings = false,
}: {
  name: string;
  avatarUrl: string | null;
  showSettings?: boolean;
}) {
  const { theme, toggle } = useTheme();
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors hover:bg-[var(--color-sidebar-accent)]',
          open && 'bg-[var(--color-sidebar-accent)]',
        )}
      >
        <Avatar src={avatarUrl} name={name} size="sm" />
        <span className="hidden text-sm text-[var(--color-sidebar-foreground)] lg:inline">{name}</span>
        <ChevronDown className="size-3.5 text-[var(--color-sidebar-muted-foreground)]" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] py-1 text-[var(--color-popover-foreground)] shadow-lg"
        >
          {showSettings ? (
            <MenuItemRow
              onClick={() => {
                setOpen(false);
                navigate('/settings');
              }}
              icon={<SettingsIcon className="size-4" />}
              label="Settings"
            />
          ) : null}
          <MenuItemRow
            onClick={() => toggle()}
            icon={theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            label={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          />
          <div className="my-1 h-px bg-[var(--color-border)]" />
          <MenuItemRow
            onClick={() => {
              setOpen(false);
              logout.mutate();
            }}
            icon={<LogOut className="size-4" />}
            label="Sign out"
            danger
          />
        </div>
      ) : null}
    </div>
  );
}

function MenuItemRow({
  onClick,
  icon,
  label,
  danger,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors',
        'hover:bg-[var(--color-surface-muted)]',
        danger && 'text-[var(--color-danger)]',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
